import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Children, isValidElement, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Button } from './Button.js';
import { Heading } from './Heading.js';
import { Stack } from './Stack.js';
import { Text } from './Text.js';
let registeredHighlighter = null;
/**
 * Register the syntax highlighter used by every <CodeBlock> in
 * the consumer's tree. Call once at boot (e.g. from `main.tsx`).
 *
 * Passing `null` clears the registration (useful in tests).
 *
 * @example
 *   import { registerHighlighter } from '@osd/ui/code-block';
 *   registerHighlighter(async (code, lang) => shiki.codeToHtml(code, { lang }));
 */
export function registerHighlighter(fn) {
    registeredHighlighter = fn;
}
async function highlight(code, lang) {
    if (registeredHighlighter === null)
        return null;
    try {
        const html = await registeredHighlighter(code, lang);
        if (typeof html !== 'string' || html.length === 0)
            return null;
        return html;
    }
    catch {
        return null;
    }
}
function resolveCode(code, children) {
    if (code !== undefined)
        return code;
    if (children === undefined)
        return '';
    let out = '';
    Children.forEach(children, (child) => {
        if (typeof child === 'string') {
            out += child;
        }
        else if (typeof child === 'number') {
            out += String(child);
        }
        else if (isValidElement(child)) {
            // Stray JSX children — likely a consumer bug. Skip silently.
        }
    });
    return out;
}
const CODE_SURFACE_WRAPPER_STYLE = {
    position: 'relative',
};
const COPY_BUTTON_STYLE = {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    zIndex: 1,
};
const COPY_LABELS = {
    idle: { button: 'Copy', ariaSuffix: '', announcement: '' },
    copied: { button: 'Copied', ariaSuffix: ' (copied)', announcement: 'Copied to clipboard' },
    error: {
        button: 'Copy failed',
        ariaSuffix: ' (failed)',
        announcement: 'Copy failed',
    },
};
function useClipboardCopy(resetMs = 2000) {
    const [status, setStatus] = useState('idle');
    const timerRef = useRef(null);
    const copy = useCallback(async (text) => {
        try {
            if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
                throw new TypeError('Clipboard API unavailable in this browser');
            }
            await navigator.clipboard.writeText(text);
            setStatus('copied');
        }
        catch {
            setStatus('error');
        }
        if (timerRef.current !== null)
            clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setStatus('idle'), resetMs);
    }, [resetMs]);
    useEffect(() => {
        return () => {
            if (timerRef.current !== null)
                clearTimeout(timerRef.current);
        };
    }, []);
    return { status, copy };
}
export function CodeBlock({ code: codeProp, children, language, title, hint, }) {
    const code = useMemo(() => resolveCode(codeProp, children), [codeProp, children]);
    const [html, setHtml] = useState(null);
    useEffect(() => {
        let cancelled = false;
        highlight(code, language).then((result) => {
            if (!cancelled)
                setHtml(result);
        });
        return () => {
            cancelled = true;
        };
    }, [code, language]);
    const ariaLabel = title !== undefined ? `${title} — ${language} code` : `${language} code`;
    const { status, copy } = useClipboardCopy();
    const labels = COPY_LABELS[status];
    return (_jsxs(Stack, { gap: 2, children: [title !== undefined || hint !== undefined ? (_jsxs("header", { children: [title !== undefined ? _jsx(Heading, { level: 3, children: title }) : null, hint !== undefined ? _jsx(Text, { muted: true, children: hint }) : null] })) : null, _jsxs("div", { style: CODE_SURFACE_WRAPPER_STYLE, children: [_jsx(Button, { variant: "ghost", onClick: () => {
                            void copy(code);
                        }, "aria-label": `Copy code to clipboard${labels.ariaSuffix}`, style: COPY_BUTTON_STYLE, className: "osd-code-block__copy", children: labels.button }), _jsx("pre", { className: "osd-code-block", "aria-label": ariaLabel, children: html !== null ? (_jsx("code", { className: `language-${language}`, dangerouslySetInnerHTML: { __html: html } })) : (_jsx("code", { className: `language-${language}`, children: code })) })] }), _jsx("span", { role: "status", "aria-live": "polite", className: "osd-sr-only", children: labels.announcement })] }));
}
//# sourceMappingURL=CodeBlock.js.map